<template>
  <div class="category-tabs-container" v-if="tab_items.length > 0">
    <nav
      aria-label="Product category tabs"
      class="category-tabs-nav">
      <button
        v-for="tab in tab_items"
        :key="tab.key"
        type="button"
        @click="SelectTab(tab.key)"
        :class="['category-tab-btn', { active: IsActiveTab(tab.key) }]">
        <span>{{ tab.label }}</span>
        <div
          v-if="IsActiveTab(tab.key)"
          class="tab-active-indicator">
        </div>
      </button>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CATEGORY_TABS, type CategoryTabKey, type CategoryTabItem } from '@/mixins/CategoryMixins.ts'

const props = withDefaults(
  defineProps<{
    modelValue?: CategoryTabKey | string
    tabs?: CategoryTabItem[]
  }>(),
  {
    modelValue: 'printers',
    tabs: () => CATEGORY_TABS
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: CategoryTabKey): void
  (e: 'change', value: CategoryTabKey): void
}>()

const tab_items = computed<CategoryTabItem[]>(() => {
  return props.tabs && props.tabs.length > 0 ? props.tabs : CATEGORY_TABS
})

function IsActiveTab(key: CategoryTabKey): boolean {
  if (key === 'printers' && (props.modelValue === 'office-copiers' || props.modelValue === 'other')) return true
  if (key === 'water-dispensers' && (props.modelValue === 'water-dispenser' || props.modelValue === 'water')) return true
  if (key === 'shredders' && props.modelValue === 'shredder') return true
  return props.modelValue === key
}

function SelectTab(key: any) {
  const raw = String(key).trim().toLowerCase()
  const resolved_key = (raw === 'office-copiers' || raw === 'other') ? 'printers'
    : (raw === 'water-dispenser' || raw === 'water') ? 'water-dispensers'
    : (raw === 'shredder') ? 'shredders'
    : raw as CategoryTabKey
  emit('update:modelValue', resolved_key)
  emit('change', resolved_key)
}
</script>

<style scoped lang="scss">
.category-tabs-container {
  width: 100%;
  position: relative;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 20px;
  background: transparent;
}

.category-tabs-nav {
  display: flex;
  align-items: center;
  gap: 28px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0 4px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.category-tab-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 4px 14px;
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  font-family: Inter, system-ui, -apple-system, sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #64748b;
  transition: color 0.15s ease;

  &:hover {
    color: #0f172a;
  }

  &.active {
    font-weight: 700;
    color: #0f172a;
  }
}

.tab-active-indicator {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2.5px;
  background: #1b5fd1;
  border-radius: 2px 2px 0 0;
}

@media (max-width: 600px) {
  .category-tabs-nav {
    gap: 18px;
  }

  .category-tab-btn {
    font-size: 14px;
    padding: 8px 2px 12px;
  }
}

@media (max-width: 400px) {
  .category-tabs-container {
    display: none !important;
  }
}
</style>
